// Route logic for the public portfolio page at `/portfolio/$profileSlug`, on
// the app origin and on the marketing-domain portfolio host alike (EPD-10104,
// EPD-12500), and for the earlier addresses that redirect to it.

import {
  publicPortfolioQueryOptions,
  publicSlugResolveQueryOptions,
} from "@/lib/queries/public-profile";
import {
  findProfileSlugRedirect,
  isPublicPageNotFound,
  type SlugLocation,
  type SlugPathPrefix,
} from "@/lib/utils/profile-slug-redirect";
import { PORTFOLIO_PATH_PREFIX } from "@repo/core";
import type { QueryClient } from "@tanstack/react-query";
import { redirect } from "@tanstack/react-router";

/**
 * `beforeLoad` for the public portfolio route (EPD-11640): a retired slug
 * redirects to the current one under the same `prefix`. The resolve is gated
 * like getPublicPortfolio (unpublished → NOT_FOUND → no redirect), so it
 * reveals nothing the page doesn't, and it only runs when the page itself 404s,
 * so a current slug costs no extra request.
 */
export async function portfolioBeforeLoad(options: {
  queryClient: QueryClient;
  slug: string;
  location: SlugLocation;
  prefix: SlugPathPrefix;
}): Promise<void> {
  const { queryClient, slug, location, prefix } = options;

  const pageMissing = await isPublicPageNotFound(() =>
    // No retries here: the page's own query retries once it mounts, so a
    // transient error shouldn't hold the navigation.
    queryClient.fetchQuery({
      ...publicPortfolioQueryOptions(slug),
      retry: false,
    }),
  );
  if (!pageMissing) return;
  const slugRedirect = await findProfileSlugRedirect({
    location,
    prefix,
    slug,
    resolve: (from) =>
      queryClient.fetchQuery(publicSlugResolveQueryOptions(from)),
  });
  if (slugRedirect) {
    throw redirect({ href: slugRedirect, replace: true });
  }
}

/**
 * `beforeLoad` for an earlier portfolio address (`/p/<slug>`, `/p2/<slug>`):
 * replace-redirect to `/portfolio/<slug>`, keeping the search and hash. The
 * edge answers these with a 301 wherever the web worker runs (EPD-12500); this
 * covers the app without it in front (local dev).
 */
export function redirectToPortfolioAddress(options: {
  slug: string;
  location: SlugLocation;
}): never {
  const { slug, location } = options;
  const hash = location.hash ? `#${location.hash}` : "";
  throw redirect({
    href: `${PORTFOLIO_PATH_PREFIX}/${encodeURIComponent(slug)}${location.searchStr}${hash}`,
    replace: true,
  });
}
